import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Package, Truck, ShieldCheck, MapPin } from "lucide-react";
import {
  registerCustomer,
  loginCustomer,
  forgotPassword,
  isCustomerLoggedIn,
} from "context/actions/customer";
import CustomSnackBar from "components/CustomSnackBar";

const BRAND = "#2F4858";
const BRAND_DARK = "#22343F";
const ACCENT = "#E8A33D";

// Backend-ийн алдааны message-ийг монгол текст рүү хөрвүүлэх
const errorText = (result) => {
  const msg = result?.message;
  if (msg === "phone_exists") return "Энэ утасны дугаар бүртгэгдсэн байна";
  if (msg === "email_exists") return "Энэ имэйл хаяг бүртгэгдсэн байна";
  if (msg === "user_nomatch") return "Бүртгэл олдсонгүй. Утас/имэйлээ шалгана уу";
  if (msg === "wrong_password") return "Нууц үг буруу байна";
  if (result?.code === 422 && Array.isArray(result?.errors))
    return result.errors.map((e) => Object.values(e)[0]).join(", ");
  if (result?.data?.errors)
    return result.data.errors.map((e) => Object.values(e)[0]).join(", ");
  return "Алдаа гарлаа. Дахин оролдоно уу";
};

const features = [
  { icon: Package, text: "Илгээмжээ бодит цагт хянах" },
  { icon: Truck, text: "Хятадаас Монгол руу хурдан хүргэлт" },
  { icon: ShieldCheck, text: "Найдвартай, аюулгүй тээвэрлэлт" },
];

const TrackAuth = () => {
  const router = useRouter();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState("");
  const [snack, setSnack] = useState({ isOpen: false, msg: "", type: "success" });

  const [showPass, setShowPass] = useState(false);

  // login
  const [identifier, setIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // register
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  useEffect(() => {
    if (isCustomerLoggedIn()) router.replace("/track/cargo");
  }, []);

  const handleTabChange = (_e, v) => {
    setTab(v);
    setAlert("");
  };

  const handleLogin = async () => {
    setAlert("");
    if (!identifier || !loginPassword) {
      setAlert("Утас/имэйл болон нууц үгээ оруулна уу");
      return;
    }
    setLoading(true);
    const result = await loginCustomer({
      identifier: identifier.trim(),
      password: loginPassword,
    });
    setLoading(false);
    if (result.code === 200) router.push("/track/cargo");
    else setAlert(errorText(result));
  };

  const handleRegister = async () => {
    setAlert("");
    if (!name || !phone || !email || !password || !confirmPassword) {
      setAlert("Бүх талбарыг бүрэн оруулна уу");
      return;
    }
    if (!/^\d{8}$/.test(phone)) {
      setAlert("Утасны дугаар 8 оронтой байх ёстой");
      return;
    }
    if (password.length < 6) {
      setAlert("Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой");
      return;
    }
    if (password !== confirmPassword) {
      setAlert("Нууц үг хоорондоо таарахгүй байна");
      return;
    }
    if (!agreed) {
      setAlert("Үйлчилгээний нөхцөлийг зөвшөөрнө үү");
      return;
    }
    setLoading(true);
    const result = await registerCustomer({ name, phone, email, password });
    setLoading(false);

    if (result.code === 201 || result.code === 200) {
      setSnack({
        isOpen: true,
        msg: "Амжилттай бүртгэгдлээ. Одоо нэвтэрнэ үү",
        type: "success",
      });
      setIdentifier(phone);
      setLoginPassword("");
      setTab(0);
      setName("");
      setPhone("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setAgreed(false);
    } else {
      setAlert(errorText(result));
    }
  };

  const handleForgot = async () => {
    const em = forgotEmail.trim();
    if (!em) return;
    setForgotLoading(true);
    await forgotPassword(em);
    setForgotLoading(false);
    setForgotSent(true);
  };

  const handleKey = (fn) => (e) => {
    if (e.key === "Enter") {
      fn();
      e.preventDefault();
    }
  };

  const passAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPass((v) => !v)} edge="end" size="small">
        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, sm: 3 },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 960,
          minHeight: { xs: "100vh", sm: 600 },
          borderRadius: { xs: 0, sm: 4 },
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* ЗҮҮН: Брэнд самбар */}
        <Box
          sx={{
            flex: { md: "0 0 44%" },
            position: "relative",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 5,
            color: "#fff",
            backgroundImage: `linear-gradient(160deg, rgba(47,72,88,0.92), rgba(34,52,63,0.96)), url(/hero-cargo.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box>
            <Box
              component="a"
              href="https://khulegcargo.mn"
              sx={{ display: "inline-block", mb: 4, textDecoration: "none" }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 26, color: "#fff", letterSpacing: 0.3 }}>
                Khuleg <Box component="span" sx={{ color: ACCENT }}>Cargo</Box>
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              Хятадаас Монгол руу
              <Box component="span" sx={{ color: ACCENT }}> хурдан хүргэлт</Box>
            </Typography>
            <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.8)" }}>
              Бүртгүүлж нэвтэрснээр өөрийн илгээмжийн төлөвийг хүссэн үедээ хянаарай.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.12)",
                    }}
                  >
                    <Icon size={20} color={ACCENT} />
                  </Box>
                  <Typography sx={{ fontSize: 14 }}>{f.text}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* БАРУУН: Форм */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, sm: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Mobile лого */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box component="a" href="https://khulegcargo.mn" sx={{ textDecoration: "none" }}>
              <Typography sx={{ fontWeight: 800, fontSize: 24, color: BRAND, letterSpacing: 0.3 }}>
                Khuleg <Box component="span" sx={{ color: ACCENT }}>Cargo</Box>
              </Typography>
            </Box>
          </Box>

          <Typography
            component="a"
            href="/"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1.5,
              fontSize: 14,
              fontWeight: 600,
              color: BRAND,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            ← Нүүр хуудас руу буцах
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND }}>
            {tab === 0 ? "Тавтай морил" : "Бүртгүүлэх"}
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 2, fontSize: 14 }}>
            {tab === 0
              ? "Ачаагаа шалгахын тулд нэвтэрнэ үү"
              : "Хэдхэн алхамаар бүртгэлээ үүсгээрэй"}
          </Typography>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 1,
              "& .MuiTabs-indicator": { backgroundColor: BRAND, height: 3 },
              "& .Mui-selected": { color: `${BRAND} !important`, fontWeight: 700 },
            }}
          >
            <Tab label="Нэвтрэх" />
            <Tab label="Бүртгүүлэх" />
          </Tabs>

          {alert && (
            <Alert severity="error" variant="outlined" sx={{ mt: 1, mb: 1 }}>
              {alert}
            </Alert>
          )}

          {/* НЭВТРЭХ */}
          {tab === 0 && !forgotMode && (
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Утасны дугаар эсвэл имэйл"
                size="small"
                fullWidth
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MapPin size={16} color={BRAND} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Нууц үг"
                type={showPass ? "text" : "password"}
                size="small"
                fullWidth
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyPress={handleKey(handleLogin)}
                InputProps={{ endAdornment: passAdornment }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleLogin}
                disabled={loading}
                sx={{
                  backgroundColor: BRAND,
                  "&:hover": { backgroundColor: BRAND_DARK },
                  py: 1.2,
                  fontWeight: 700,
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Нэвтрэх"}
              </Button>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setForgotMode(true);
                    setForgotSent(false);
                    setAlert("");
                  }}
                  sx={{ textTransform: "none", color: BRAND, fontSize: 13 }}
                >
                  Нууц үг мартсан?
                </Button>
              </Box>
            </Box>
          )}

          {/* НУУЦ ҮГ СЭРГЭЭХ */}
          {tab === 0 && forgotMode && (
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              {forgotSent ? (
                <Alert severity="success" variant="outlined">
                  Хэрэв энэ имэйл бүртгэлтэй бол нууц үг сэргээх холбоос илгээгдсэн. Имэйлээ (спам хавтсыг ч) шалгана уу.
                </Alert>
              ) : (
                <>
                  <Typography sx={{ fontSize: 14, color: "#6E7480" }}>
                    Бүртгэлтэй имэйл хаягаа оруулна уу. Танд нууц үг сэргээх холбоос илгээнэ.
                  </Typography>
                  <TextField
                    label="Имэйл"
                    type="email"
                    size="small"
                    fullWidth
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    onKeyPress={handleKey(handleForgot)}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleForgot}
                    disabled={forgotLoading}
                    sx={{ backgroundColor: BRAND, "&:hover": { backgroundColor: BRAND_DARK }, py: 1.2, fontWeight: 700 }}
                  >
                    {forgotLoading ? <CircularProgress size={22} color="inherit" /> : "Сэргээх холбоос илгээх"}
                  </Button>
                </>
              )}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setForgotMode(false);
                    setForgotSent(false);
                  }}
                  sx={{ textTransform: "none", color: "#6E7480", fontSize: 13 }}
                >
                  ← Нэвтрэх рүү буцах
                </Button>
              </Box>
            </Box>
          )}

          {/* БҮРТГҮҮЛЭХ */}
          {tab === 1 && (
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Нэр"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                label="Утасны дугаар"
                type="tel"
                size="small"
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <TextField
                label="Имэйл хаяг"
                size="small"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Нууц үг"
                type={showPass ? "text" : "password"}
                size="small"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{ endAdornment: passAdornment }}
              />
              <TextField
                label="Нууц үг давтах"
                type={showPass ? "text" : "password"}
                size="small"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKey(handleRegister)}
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={
                  confirmPassword.length > 0 && password !== confirmPassword
                    ? "Нууц үг таарахгүй байна"
                    : ""
                }
              />

              <FormControlLabel
                sx={{ mt: -0.5 }}
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    sx={{ color: BRAND, "&.Mui-checked": { color: BRAND } }}
                  />
                }
                label={
                  <Typography variant="body2">
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTerms(true);
                      }}
                      sx={{ mr: 0.5, color: BRAND, fontWeight: 600 }}
                    >
                      Үйлчилгээний нөхцөл
                    </Link>
                    -ийг уншиж зөвшөөрч байна
                  </Typography>
                }
              />

              <Button
                variant="contained"
                size="large"
                onClick={handleRegister}
                disabled={loading || !agreed}
                sx={{
                  backgroundColor: BRAND,
                  "&:hover": { backgroundColor: BRAND_DARK },
                  py: 1.2,
                  fontWeight: 700,
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Бүртгүүлэх"}
              </Button>
            </Box>
          )}

          <Button
            sx={{ mt: 2, color: "text.secondary" }}
            fullWidth
            onClick={() => router.push("/")}
          >
            ← Нүүр хуудас руу буцах
          </Button>
        </Box>
      </Paper>

      {/* Үйлчилгээний нөхцөл */}
      <Dialog
        open={openTerms}
        onClose={() => setOpenTerms(false)}
        scroll="paper"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: BRAND }}>
          Үйлчилгээний нөхцөл
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ whiteSpace: "pre-line" }}>
            {`1. ЕРӨНХИЙ НӨХЦӨЛ

1.1. Энэхүү үйлчилгээний нөхцөл нь ХҮЛЭГ КАРГО (цаашид "Карго" гэх)-ын ачаа тээвэр, зуучлал, хадгалалт болон түүнтэй холбоотой бүх төрлийн харилцааг зохицуулна.

1.2. Карго нь Бүгд Найрамдах Хятад Ард Улсын нутаг дэвсгэрээс (онлайн худалдааны платформууд болон нийлүүлэгчдээс) хэрэглэгчийн захиалсан бараа бүтээгдэхүүнийг хүлээн авч, Эрээн хот дахь агуулахад төвлөрүүлэн Монгол Улсын хил хүртэл болон цааш хэрэглэгчид хүргэх тээвэр зуучлалын үйлчилгээ үзүүлнэ.

1.3. Карго нь барааны үйлдвэрлэгч, худалдагч, эсвэл нийлүүлэгч биш бөгөөд хэрэглэгчийн захиалсан барааны чанар, үйлдвэрийн доголдол, анхны эвдрэл гэмтэлд хариуцлага хүлээхгүй.

1.4. Хэрэглэгч нь энэхүү үйлчилгээний нөхцөлийг хүлээн зөвшөөрсөн тохиолдолд үйлчилгээ авах эрх үүснэ.

2. ХЭРЭГЛЭГЧИЙН ЭРХ, ҮҮРЭГ

2.1. Хэрэглэгч нь дараах мэдээллийг үнэн зөв өгнө:
• Захиалгын линк, трэк код
• Хүлээн авагчийн мэдээлэл
• Холбоо барих утас, хаяг

2.2. Хэрэглэгч нь хориотой болон эрсдэлтэй бараа захиалахгүй байх үүрэгтэй.

2.3. Хэрэглэгч нь "Хэврэг бараа"-ны эрсдэлийг бүрэн ойлгож, захиалга хийхдээ өөрөө хариуцлага хүлээнэ.

3. КАРГОНЫ ЭРХ, ҮҮРЭГ

3.1. Карго нь барааг Эрээн агуулахад хүлээн авч, бүртгэн, стандарт нөхцөлөөр хадгалж, тээвэрлэлтэд шилжүүлнэ.

3.2. Карго нь өөрийн буруутай үйлдэл (буруу илгээх, алдах, агуулахад гэмтээх)-ээс үүдэлтэй хохирлыг хариуцна.

3.3. Карго нь дараах нөхцөлд хариуцлага хүлээхгүй:
• Барааны үйлдвэрийн доголдол
• Дотор гэмтэлтэй боловч гадна сав баглаа гэмтээгүй тохиолдол
• Тээврийн явцад үүсэх доргилт, чичиргээ, температур, чийгшлийн нөлөө (хэврэг бараа)
• Хэрэглэгчийн буруу мэдээлэл, буруу захиалга

3.4. Шил, шаазан, толь, дэлгэц, LCD, электрон нарийн төхөөрөмж зэрэг "хэврэг бараа" нь өндөр эрсдэлтэй ангилалд хамаарна.

3.5. Карго нь хэврэг барааны тээвэрлэлтийн явцад үүсч болзошгүй дотор гэмтэл, хагарал, ажиллагааны доголдолд хариуцлага хүлээхгүй.

3.6. Хэрэглэгч нь хэврэг бараа захиалахдаа эрсдэлийг бүрэн хүлээн зөвшөөрсөнд тооцогдоно.

4. ХОРИОТОЙ БАРАА

4.1. Каргогоор тээвэрлэхэд тохиромжгүй доорх төрлийн ачааг Эрээн агуулахад хүлээж авахгүй болохыг анхаарна уу. Үүнд:
• Амархан хагарч эвдрэх зүйлс (шил, шилэн нүүртэй эдлэл, шилэн савалгаатай гоо сайхны бүтээгдэхүүн, шаазан, толь, ваар, хуванцар, чулуун тавцан г.м)
• Доргилтонд гэмтэх магадлалтай технологийн бүтээгдэхүүн (компьютер, таблет, түүний эд анги, дэлгэц, телевизор г.м)
• Бүх төрлийн хүнсний бүтээгдэхүүн (жимс жимсгэнэ, чихэр, хадгалалтын горим алдагдах зүйлс г.м)

4.2. Монгол улсын хуулийн дагуу гаалиар нэвтрүүлэхийг хориглосон бүх төрлийн бараа бүтээгдэхүүнийг тээвэрлэх боломжгүй. Үүнд:
• Согтууруулах ундаа
• Тамхи
• Стратегийн хүнс
• Жижиглэн савалгаатай, 25%-с дээш концентрацитай, хүнсний зориулалттай цууны хүчил
• Зохих зөвшөөрөлгүй хүний болон малын эм, эмийн бүтээгдэхүүн, эмнэлгийн хэрэгсэл
• Тэсэрч дэлбэрэх бодис, тэсэлгээний хэрэгсэл, түүхий эд
• Мод, сөөгний тарьц, суулгац
• Мал, амьтан
• Химийн хорт бодис
• Мансууруулах болон сэтгэцэд нөлөөт бодис
• Аюултай хог хаягдал
• Асбест агуулсан тоормозны накладка
• Цөмийн бодис
• Галт зэвсэг, сум, галт зэвсэгтэй адилтгах хэрэгсэл
• Бүх төрлийн шатахуун
• Пиротехникийн хэрэгсэл
• Хууль тогтоомжоор хориглосон бусад зүйл

4.3. Тусгай зөвшөөрлийн лиценз шаарддаг бараа бүтээгдэхүүн захиалах тохиолдолд хэрэглэгч тухайн бүтээгдэхүүний тусгай зөвшөөрлийн лицензтэй бол КАРГО-оор Монгол улсын гааль хүртэлх гүйцэтгэлийг гүйцэтгүүлж болно.

5. ТЭЭВЭРЛЭЛТИЙН ХУГАЦАА

5.1. Бараа бүтээгдэхүүн КАРГО-ны Эрээн хот дахь агуулахад хүргэгдэн, Монгол улсруу ачигдсанаас хойш ердийн нөхцөлд 2-7 хоног дотор УБ хотод ирнэ. Бүх нийтийн баяр ёслол, гэнэтийн давагдашгүй хүчин зүйл үүссэн тохиолдолд дээрх хугацаа сунгагдаж болно. Гэнэтийн болон давагдашгүй хүчин зүйл гэдэгт гал түймэр, газар хөдлөлт, салхи шуурга, аянга цахилгаан, үер усны гамшиг, нийтийг хамарсан үймээн самуун, эмх замбараагүй байдал, ажил хаялт, эрчим хүчний тасалдал, эсэргүүцлийн хөдөлгөөн, гоц халдварт өвчин, дайн, террорист халдлага, төрийн эрх бүхий байгууллагын шийдвэр, хорио, цээр зэрэг талуудын хүсэл зоригоос үл хамаарах шалтгаанууд багтана.

6. МАРГААН ШИЙДВЭРЛЭХ ЖУРАМ

6.1. Бараатай холбоотой маргаан үүссэн тохиолдолд хэрэглэгч дараах баримтыг бүрдүүлнэ:
• Бараа хүлээн авсан үеийн бичлэг (unboxing video)
• Гадна сав баглаа боодлын зураг
• Гэмтлийн тодорхой зураг/видео

6.2. Карго нь маргааныг дараах үндэслэлээр шийдвэрлэнэ:
• Каргогийн буруутай үйлдэл нотлогдсон эсэх
• Гадна сав баглаа боодлын бүрэн бүтэн байдал
• Хятад нийлүүлэгчийн хариуцлагын хүрээ

6.3. Хэрэв бараа гадна сав баглаа боодол бүрэн, гэмтэлгүй ирсэн боловч дотор эвдрэл илэрсэн тохиолдолд энэ нь үйлдвэрлэгчийн доголдол эсвэл тээврийн эрсдэлд хамаарах бөгөөд Карго хариуцлага хүлээхгүй.

6.4. Карго нь шаардлагатай тохиолдолд нийлүүлэгчтэй холбогдож хэрэглэгчийн өмнөөс гомдол гаргах, буцаалт/солилцооны хүсэлт илгээхэд туслалцаа үзүүлж болно (баталгаа биш).

7. БУСАД

7.1. Карго нь үйлчилгээний нөхцөлийг урьдчилан мэдэгдэлгүйгээр шинэчлэх эрхтэй.

7.2. Хэрэглэгч нь үйлчилгээ ашигласнаар энэхүү нөхцөлийг бүрэн хүлээн зөвшөөрсөнд тооцогдоно.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTerms(false)}>Хаах</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: BRAND, "&:hover": { backgroundColor: BRAND_DARK } }}
            onClick={() => {
              setAgreed(true);
              setOpenTerms(false);
            }}
          >
            Зөвшөөрөх
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snack.isOpen}
        msg={snack.msg}
        handleClose={() => setSnack({ ...snack, isOpen: false })}
        type={snack.type}
      />
    </Box>
  );
};

export default TrackAuth;
